<script lang="ts">
export default {
  name: 'DocumentModal',
};

interface Document {
  name: string;
  type: DocumentType;
  accountId: number;
  policyId?: number;
  policyNumber?: string;
  file: File;
  selected: boolean;
  readyForUpload: boolean;
}

interface Options {
  documentTypes: DocumentType[];
}

interface SelectedOptions {
  documentType?: DocumentType;
}
</script>

<script setup lang="ts">
import { reactive, ref, computed, watch } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import { CircleCheck, CircleX, CloudUpload, OctagonAlert, Trash2, Pencil, Users, Eye, Download } from '@lucide/vue';
import { DocumentType, Document as Doc, UpdateDocumentRequest } from '@/types/v2/Document';
import { documentService } from '@/services/v2/DocumentService';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { useRouter } from 'vue-router';
import { relativeDateTime } from '@/utilities/Dates';
import { formatDate } from '@/utilities/Format';
import DocumentEditModal from '@/components/v2/DocumentEditModal.vue';
import { IsMarketUser, IsUserAClient } from '@/utilities/AuthUtils';
import Store from '@/store';
import DocumentViewerModal from '@/components/DocumentViewerModal.vue';

const router = useRouter();

// -----
// refs
// -----
const appModal = ref<typeof AppModal>();
const documentsSectionRef = ref<HTMLElement | null>(null);
const documentTypesSectionRef = ref<HTMLElement | null>(null);
const draggingOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const documents = ref([] as Partial<Document>[]);
const existingDocuments = ref([] as Doc[]);
const selectedOptions = reactive<SelectedOptions>({});
const options = reactive<Options>({
  documentTypes: [] as DocumentType[],
});

// Document viewer state
const showDocumentModal = ref(false);
const viewDocumentFileName = ref('');
const viewDocument = ref<Doc | undefined>(undefined);

const docData = reactive<{ accountId: number; policyId: number; mainPolicyNumber: string }>({
  accountId: 0,
  policyId: 0,
  mainPolicyNumber: '',
});

// ---------
// computed
// ---------
const policyDocumentTypes = computed(() => {
  return options.documentTypes
    .filter((documentType) => documentType.domainName === 'Policy')
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    }) as DocumentType[];
});

const selectedDocuments = computed(() => {
  return documents.value.filter((document) => document.selected);
});

const accountDocumentTypes = computed(() => {
  return options.documentTypes
    .filter((documentType) => documentType.domainName === 'Account')
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
});

const isClientRole = computed(() => {
  return IsUserAClient(Store.state.user);
});

const isMarketRole = computed(() => {
  return IsMarketUser(Store.state.user);
});

const existingDocumentList = computed(() => {
  if (isClientRole.value) {
    return existingDocuments.value.filter((d) => d.published);
  }
  return existingDocuments.value;
});

// ---------
// watchers
// ---------
watch(documents, async () => {
  removeEventListener('click', handleClickOutside);
  addEventListener('click', handleClickOutside);
});

watch(selectedDocuments, (newSelectedDocuments) => {
  // no documents are selected
  if (newSelectedDocuments.length === 0) {
    selectedOptions.documentType = undefined;
  }

  // only one document is selected
  else if (newSelectedDocuments.length === 1) {
    selectedOptions.documentType = newSelectedDocuments[0]?.type;
  }

  // multiple documents are selected
  else if (newSelectedDocuments.length > 1) {
    // if they all have the same document type, set the selected document type
    if (
      newSelectedDocuments[0]?.type &&
      newSelectedDocuments.reduce((acc, document) => acc && document?.type === newSelectedDocuments[0]?.type, true)
    ) {
      selectedOptions.documentType = newSelectedDocuments[0].type;
    } else {
      selectedOptions.documentType = undefined;
    }
  }
});

// ---------
// handlers
// ---------
async function open(accountId: number, policyId: number, mainPolicyNumber: string): Promise<void> {
  appModal.value?.open();

  docData.policyId = policyId;
  docData.accountId = accountId;
  docData.mainPolicyNumber = mainPolicyNumber;

  await Promise.all([loadDocumentTypes(), loadDocuments()]);
}

function close() {
  // cleanup
  existingDocuments.value = [];
  documents.value = [];

  appModal.value?.close();
}

function handleFileDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = true;
}

function handleFileDragLeave(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;
}

function handleFileDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;

  if (e.dataTransfer?.items) {
    // use the DataTransferItemList interface if available
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.items].forEach((item) => {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          droppedDocuments.push({
            name: file.name,
            file: file,
            policyId: Number(docData.policyId),
          });
        }
      }
    });
    documents.value = [...documents.value, ...droppedDocuments];
  } else if (e.dataTransfer?.files) {
    // otherwise use the DataTransfer interface
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.files].forEach((file) => {
      droppedDocuments.push({
        name: file.name,
        file: file,
      });
    });
    documents.value = [...documents.value, ...droppedDocuments];
  }

  // select the document if only one was dropped
  if (documents.value.length === 1) {
    documents.value[0].selected = true;
  }
}

function handleFilesSelected(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files) {
    const selectedDocuments = [...target.files].map((file) => ({
      name: file.name,
      file: file,
      policyId: Number(docData.policyId),
      policyNumber: docData.mainPolicyNumber,
    }));
    documents.value = [...documents.value, ...selectedDocuments];

    // select the document if only one was selected
    if (documents.value.length === 1) {
      documents.value[0].selected = true;
    }
  }

  e.preventDefault();
  e.stopPropagation();
}

function handleSelectFiles() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

function handleRemoveDocumentType(document: Partial<Document>) {
  selectedOptions.documentType = undefined;
  document.type = undefined;
  validateDocuments();
}

function validateDocuments() {
  documents.value.forEach((document) => {
    if (!document.type) {
      document.readyForUpload = false;
      return;
    } else if (isPolicyDocumentType(document.type) && !document.policyId) {
      document.readyForUpload = false;
      return;
    }
    document.readyForUpload = true;
  });
}

function isPolicyDocumentType(documentType: DocumentType): boolean {
  return policyDocumentTypes.value.some((type) => type === documentType);
}

function handleDeleteDocument(documentIndex: number) {
  documents.value = documents.value.filter((_, index) => index !== documentIndex);
}

const readyForUpload = computed(() => {
  return documents.value.length > 0 && documents.value.every((document) => document.readyForUpload);
});

function handleSelectDocumentType(documentType: DocumentType) {
  if (selectedOptions.documentType === documentType) {
    selectedOptions.documentType = undefined;
  } else {
    selectedOptions.documentType = documentType;
  }

  selectedDocuments.value.map((document) => {
    document.type = selectedOptions.documentType;
  });

  validateDocuments();
}

function handleClickOutside(event: MouseEvent) {
  if (
    documentsSectionRef.value &&
    !documentsSectionRef.value.contains(event.target as Node) &&
    documentTypesSectionRef.value &&
    !documentTypesSectionRef.value.contains(event.target as Node)
  ) {
    documents.value = documents.value.map((document) => ({ ...document, selected: false }));
  }
}

async function handleUpload() {
  if (!readyForUpload.value) return;

  const requests = documents.value.map((document) => {
    return documentService.uploadDocumentForPolicyOrAccount({
      domain: document.type!.domainName,
      type: document.type!.name,
      displayName: document.name,
      fileName: document.name!,
      accountId: docData.accountId,
      policyId: document.policyId,
      document: document.file as File,
    });
  });

  try {
    await Promise.all(requests);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Documents upload successful' });
    close();
    router.go(0);
  } catch (e) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to upload documents', color: 'error' });
    console.error(e);
  }
}

async function loadDocuments(): Promise<void> {
  if (existingDocuments.value.length === 0) {
    const policyId = docData.policyId;
    existingDocuments.value = await documentService.getDocumentsByPolicy(docData.accountId, policyId);
  }
}

async function loadDocumentTypes(): Promise<void> {
  if (options.documentTypes.length === 0) {
    options.documentTypes = await documentService.getDocumentTypes();
  }
}

function isDocumentTypeViewable(document: Doc): boolean {
  return document.fileName.endsWith('.pdf') || document.fileName.endsWith('.docx')
}

async function downloadDocumentOrBinder(doc: Doc): Promise<void> {
  if (!doc.documentId) {
    console.error('source document key is required');
    return;
  }
  if (!doc.accountId) {
    console.error('account id is required');
    return;
  }

  const target = await documentService.downloadDocumentFile(doc.accountId, doc.documentId);
  const anchorElement = document.createElement('a');
  anchorElement.href = target;
  anchorElement.download = doc.displayName || 'download';
  anchorElement.setAttribute('target', '_blank');
  anchorElement.click();
  anchorElement.remove();
}

async function openDocument(doc: Doc, event?: Event) {
  if (event) event.stopPropagation();
  if (!doc.documentId || !doc.accountId) {
    console.error('Document id and account id are required to view');
    return;
  }
  viewDocumentFileName.value = doc.displayName || 'Document';
  viewDocument.value = doc;
  showDocumentModal.value = true;
}

async function fetchDocument(convertToPdf: boolean): Promise<string> {
  if (!viewDocument.value?.documentId || !viewDocument.value?.accountId) {
    console.error('No document selected for viewing');
    return '';
  }
  try {
    return await documentService.downloadDocumentFile(viewDocument.value.accountId, viewDocument.value.documentId, convertToPdf);
  } catch (e) {
    console.error('Failed to fetch document for viewing', e);
    return '';
  }
}

async function updateDocumentInfo(document: Doc, request?: UpdateDocumentRequest) {
  if (request) {
    await documentService.updateDocument(document.documentId, request);
    document.documentTypeId = request.documentTypeId ?? document.documentTypeId;
    document.documentTypeName =
      options.documentTypes.find((t) => t.id === request.documentTypeId)?.name ?? document.documentTypeName;
    document.displayName = request.displayName ?? document.displayName;
  }
}

defineExpose({
  open,
  close,
});
</script>

<template>
  <slot name="activator" :open="open"></slot>
  <AppModal
    ref="appModal"
    size="lg"
    :show="false"
    :loading="false"
    :handleCancel="close"
    modalClass="max-w-[800px] overflow-y-auto z-100">
    <template #title>
      <p class="text-xl font-normal text-primary">Documents</p>
      <p class="pl-1 text-sm text-primary italic">Policy: {{ docData.mainPolicyNumber }}</p>
    </template>

    <template #content>
      <!-- existing documents for policy -->
      <div
        v-if="existingDocuments.length > 0"
        class="mx-4 mb-4 rounded-lg border-2 border-solid border-gray-300 pt-0.5">
        <table class="table table-xs">
          <thead>
            <tr class="border-b-2 border-solid border-gray-300">
              <th>Type</th>
              <th v-if="!isClientRole"><!-- published status --></th>
              <th>Name</th>
              <th>Uploaded</th>
              <th v-if="!isClientRole"><!-- actions --></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="doc in existingDocumentList" :key="doc.documentId">
              <td>{{ doc.documentTypeName }}</td>
              <th v-if="!isClientRole">
                <div
                  v-if="doc.published"
                  data-tip="This document is published and viewable by client users"
                  class="tooltip tooltip-right top-0.5 z-50 before:max-w-sm">
                  <Users class="text-gray-500" :size="16" />
                </div>
              </th>
              <td>
                <a class="flex items-center hover:cursor-pointer" @click="isDocumentTypeViewable(doc) ? openDocument(doc, $event) : downloadDocumentOrBinder(doc)">
                  <span>{{ doc.displayName }}</span>
                </a>
              </td>
              <td class="flex flex-col items-start justify-center">
                <span>{{ relativeDateTime(doc.createdByDate) }}</span>
                <span class="text-xs text-gray-600">{{ formatDate(doc.createdByDate) }}</span>
              </td>
              <td v-if="!isClientRole && !isMarketRole">
                <div class="flex justify-end gap-1">
                  <button
                    v-if="isDocumentTypeViewable(doc)"
                    @click.stop="openDocument(doc, $event)"
                    class="rounded-md p-1 text-gray-600 transition-all duration-200 hover:bg-base-300"
                    title="View document">
                    <Eye class="h-4 w-4" />
                  </button>
                  <button
                    @click.stop="downloadDocumentOrBinder(doc)"
                    class="rounded-md p-1 text-gray-600 transition-all duration-200 hover:bg-base-300"
                    title="Download document">
                    <Download class="h-4 w-4" />
                  </button>
                  <DocumentEditModal
                    ref="editDocumentModal"
                    title="Edit Document"
                    :is-user-allowed="true"
                    :document="doc"
                    :policy-document-types="policyDocumentTypes"
                    :show="false"
                    :include-tags="false"
                    @update="
                    (request) => {
                      updateDocumentInfo(doc, request);
                    }
                  ">
                    <template v-slot:activator="{ open }">
                      <button
                        @click="open"
                        class="rounded-md p-1 text-gray-600 transition-all duration-200 hover:bg-base-300">
                        <Pencil class="h-4 w-4"/>
                      </button>
                    </template>
                  </DocumentEditModal>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- drag and drop -->
      <div
        v-if="!isClientRole && !isMarketRole"
        class="p-4 py-0"
        @dragover="handleFileDragOver"
        @drop="handleFileDrop"
        @dragleave="handleFileDragLeave">
        <div
          class="rounded-lg border-2 border-dashed border-gray-400 p-2 transition-all"
          :class="{ 'bg-blue-100': draggingOver }">
          <div class="flex h-full flex-col items-center justify-center">
            <CloudUpload class="text-gray-400" />
            <div class="text-xs font-bold text-gray-400">Drag and drop files here</div>
            <div class="text-xs text-gray-400">or</div>
            <input ref="fileInput" type="file" multiple class="hidden" @change="handleFilesSelected" />
            <button class="btn btn-xs btn-secondary" @click.stop="handleSelectFiles">Select Files</button>
          </div>
        </div>
      </div>

      <!-- documents -->
      <div class="flex flex-col gap-2 p-4 py-2" ref="documentsSectionRef">
        <div v-for="(document, index) in documents" :key="index">
          <div
            class="flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-200 p-1 shadow-md transition-all"
            :class="{
              'border-secondary bg-blue-50 shadow-none hover:bg-blue-50': document.selected,
              'hover:bg-gray-50': !document.selected,
            }"
            @click="document.selected = !document.selected">
            <OctagonAlert v-if="!document.readyForUpload" :size="16" class="text-red-500" />
            <CircleCheck v-else :size="20" class="text-green-500" />
            <div class="flex-1 overflow-hidden text-xs text-ellipsis text-gray-600 italic">
              {{ document.name }}
            </div>
            <div class="flex flex-col items-end gap-2">
              <div v-if="document.type" class="relative badge bg-secondary/30 p-2 badge-md text-xs">
                {{ document.type.name }}
                <CircleX
                  :size="12"
                  class="absolute -top-1 -right-1 ml-1 text-red-500"
                  @click.stop="handleRemoveDocumentType(document)" />
              </div>
              <div v-else class="text-[11px] text-red-500">Please select a document type..</div>
            </div>
            <div
              class="group ml-5 flex h-6 w-6 items-center justify-center rounded-lg border border-solid border-gray-400 transition-all hover:border-red-300"
              @click="handleDeleteDocument(index)">
              <Trash2 :size="14" class="text-gray-400 transition-all group-hover:stroke-red-500" />
            </div>
          </div>
        </div>
      </div>

      <!-- document types -->
      <div class="p-4 pt-0" ref="documentTypesSectionRef" v-if="documents.length > 0">
        <div
          class="relative flex w-full flex-col gap-3 rounded-lg border border-solid border-gray-200 bg-primary/10 p-4 shadow-md">
          <div
            v-if="selectedDocuments.length === 0"
            class="absolute h-full w-full cursor-not-allowed rounded-lg bg-gray-100/30 transition-all"></div>
          <div class="flex flex-col gap-1">
            <div class="text-xs font-semibold text-gray-500">Account Document Types</div>
            <div class="flex flex-wrap gap-1 pl-1">
              <div
                v-for="documentType in accountDocumentTypes"
                :key="documentType.id"
                class="w-fit-content cursor-pointer rounded-lg border border-solid border-gray-300 p-1 shadow-xs transition-all"
                :class="{
                  'bg-secondary/30': documentType === selectedOptions.documentType,
                  'hover:bg-gray-50': documentType !== selectedOptions.documentType,
                }"
                @click="handleSelectDocumentType(documentType)">
                <div class="text-xs text-gray-600">{{ documentType.name }}</div>
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <div class="text-xs font-semibold text-gray-500">Policy Document Types</div>
            <div class="flex flex-wrap gap-2 pl-1">
              <div
                v-for="documentType in policyDocumentTypes"
                :key="documentType.id"
                class="w-fit-content cursor-pointer rounded-lg border border-solid border-gray-300 p-1 shadow-xs transition-all"
                :class="{
                  'bg-secondary/30': documentType === selectedOptions.documentType,
                  'hover:bg-gray-50': documentType !== selectedOptions.documentType,
                }"
                @click="handleSelectDocumentType(documentType)">
                <div class="text-xs text-gray-600">{{ documentType.name }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #actions>
      <button class="btn btn-sm" @click="close">{{ documents.length > 0 ? 'Cancel' : 'Close' }}</button>
      <button
        v-show="documents.length > 0"
        class="btn text-white btn-sm btn-primary"
        :class="{ 'btn-disabled': !readyForUpload }"
        @click="handleUpload">
        Upload
      </button>
    </template>
  </AppModal>

  <DocumentViewerModal
    :show="showDocumentModal"
    :fileName="viewDocumentFileName"
    :fetchDocument="fetchDocument"
    @update:show="showDocumentModal = $event"
    @close="showDocumentModal = false" />
</template>
